import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';

/* ── tokens ── */
const T = {
  orange:'#F04E1A', orangeD:'#C83E12', yellow:'#F5C842',
  ink:'#1C1612', bg:'#FDFAF6', surface:'#FFFFFF',
  border:'#EAE4DC', muted:'#8A7E72', subtle:'#B5A99A',
  g1:'#F8F5F1', g2:'#EAE4DC',
  green:'#16A34A', red:'#DC2626',
};
const FONT = "'Inter',-apple-system,BlinkMacSystemFont,sans-serif";
const DISPLAY = "'Bangers','Arial Black',cursive";

const Logo = ({ size = 56 }) => (
  <svg width={size} height={size} viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="512" height="512" rx="100" fill="#FDFAF6"/>
    <rect width="512" height="512" rx="100" fill="none" stroke="#EAE4DC" strokeWidth="6"/>
    <rect x="64"  y="330" width="384" height="58" rx="10" fill="#F04E1A"/>
    <rect x="64"  y="330" width="12"  height="58" rx="4"  fill="#C83E12"/>
    <rect x="80"  y="264" width="352" height="58" rx="10" fill="#F5C842"/>
    <rect x="80"  y="264" width="12"  height="58" rx="4"  fill="#D4A800"/>
    <rect x="64"  y="198" width="384" height="58" rx="10" fill="#3B6FBF"/>
    <rect x="64"  y="198" width="12"  height="58" rx="4"  fill="#2A509A"/>
    <rect x="96"  y="132" width="280" height="58" rx="10" fill="#5A9E7A"/>
    <rect x="96"  y="132" width="12"  height="58" rx="4"  fill="#3D7A5A"/>
    <ellipse cx="370" cy="104" rx="90" ry="60" fill="#FFFFFF" stroke="#F04E1A" strokeWidth="8"/>
    <path d="M322 152 L296 188 L338 164 Z" fill="#FFFFFF" stroke="#F04E1A" strokeWidth="8" strokeLinejoin="round" paintOrder="stroke"/>
    <circle cx="340" cy="104" r="10" fill="#F04E1A"/>
    <circle cx="370" cy="104" r="10" fill="#F04E1A"/>
    <circle cx="400" cy="104" r="10" fill="#F04E1A"/>
  </svg>
);

const Field = ({ label, children }) => (
  <div style={{ marginBottom: '14px' }}>
    <label style={{ display:'block', fontSize:'12px', fontWeight:600, color:T.muted, marginBottom:'6px', fontFamily:FONT, letterSpacing:'.01em' }}>
      {label}
    </label>
    {children}
  </div>
);

const inputBase = {
  width:'100%', padding:'11px 14px',
  border:`1.5px solid ${T.border}`, borderRadius:'10px',
  fontFamily:FONT, fontSize:'15px', background:T.surface, color:T.ink,
  outline:'none', transition:'border-color .15s, box-shadow .15s',
};
const onFocus = e => { e.target.style.borderColor=T.orange; e.target.style.boxShadow=`0 0 0 3px rgba(240,78,26,.12)`; };
const onBlur  = e => { e.target.style.borderColor=T.border; e.target.style.boxShadow='none'; };

export default function LoginPage() {
  const { loginEmail, registerEmail, loginGoogle } = useAuth();
  const [mode, setMode]     = useState('login');
  const [email, setEmail]   = useState('');
  const [pw, setPw]         = useState('');
  const [name, setName]     = useState('');
  const [err, setErr]       = useState('');
  const [busy, setBusy]     = useState(false);
  const [resetOk, setROk]   = useState(false);

  const clear = () => setErr('');
  const errMsg = code => ({
    'auth/user-not-found':      'Aucun compte avec cet email.',
    'auth/wrong-password':      'Mot de passe incorrect.',
    'auth/invalid-credential':  'Email ou mot de passe incorrect.',
    'auth/email-already-in-use':'Cet email est déjà utilisé.',
    'auth/weak-password':       'Mot de passe trop court (6 car. min).',
    'auth/invalid-email':       'Email invalide.',
  }[code] || 'Une erreur est survenue, réessayez.');

  const submit = async e => {
    e.preventDefault(); clear(); setBusy(true);
    try {
      if (mode === 'login') await loginEmail(email, pw);
      else {
        if (!name.trim()) { setErr('Entrez votre prénom.'); setBusy(false); return; }
        await registerEmail(email, pw, name);
      }
    } catch (e) { setErr(errMsg(e.code)); }
    setBusy(false);
  };

  const google = async () => {
    clear(); setBusy(true);
    try { await loginGoogle(); } catch { setErr('Connexion Google annulée.'); }
    setBusy(false);
  };

  const reset = async e => {
    e.preventDefault(); clear(); setBusy(true);
    try {
      const { sendPasswordResetEmail } = await import('firebase/auth');
      const { auth } = await import('../firebase');
      await sendPasswordResetEmail(auth, email);
      setROk(true);
    } catch { setErr('Email introuvable.'); }
    setBusy(false);
  };

  return (
    <div style={{
      minHeight:'min(100vh, 100dvh)',
      display:'flex', alignItems:'stretch',
      fontFamily: FONT,
    }}>
      {/* ── Panneau gauche décoratif (desktop uniquement) ── */}
      <div style={{
        display:'none',
        flex:'0 0 420px',
        background:`linear-gradient(160deg,${T.orangeD} 0%,${T.orange} 45%,#FF8C52 100%)`,
        padding:'3rem',
        flexDirection:'column',
        justifyContent:'space-between',
        position:'relative',
        overflow:'hidden',
      }} className="login-panel">
        {/* Pattern halftone */}
        <div style={{ position:'absolute', inset:0, backgroundImage:`radial-gradient(circle,rgba(255,255,255,.18) 1.5px,transparent 1.5px)`, backgroundSize:'22px 22px' }}/>
        {/* Onomatopées décoratives */}
        {[{t:'POW!',x:'8%',y:'12%',r:-12,s:'3rem',o:.22},{t:'BAM!',x:'60%',y:'5%',r:8,s:'2.2rem',o:.18},{t:'ZAP!',x:'5%',y:'60%',r:6,s:'2.6rem',o:.20},{t:'★',x:'72%',y:'55%',r:18,s:'3.5rem',o:.25},{t:'BOOM!',x:'15%',y:'80%',r:-8,s:'2rem',o:.18}].map((d,i)=>(
          <div key={i} style={{position:'absolute',left:d.x,top:d.y,fontFamily:DISPLAY,fontSize:d.s,color:`rgba(255,255,255,${d.o})`,transform:`rotate(${d.r}deg)`,letterSpacing:'.05em',userSelect:'none'}}>{d.t}</div>
        ))}
        <div style={{position:'relative'}}>
          <Logo size={68}/>
        </div>
        <div style={{position:'relative'}}>
          <div style={{fontFamily:DISPLAY,fontSize:'3.5rem',letterSpacing:'.05em',color:'#fff',lineHeight:.95,marginBottom:'16px',textShadow:'0 2px 8px rgba(0,0,0,.2)'}}>Bulléthèque</div>
          <p style={{fontSize:'15px',color:'rgba(255,255,255,.82)',lineHeight:1.6,fontWeight:500}}>
            Cataloguez, organisez et valorisez votre collection de bandes dessinées. Photos, défauts, valeur estimée — tout en un.
          </p>
          <div style={{display:'flex',gap:'20px',marginTop:'2rem'}}>
            {[['📚','Albums'],['🎨','Photos'],['📊','Statistiques']].map(([ic,lb])=>(
              <div key={lb} style={{textAlign:'center'}}>
                <div style={{fontSize:'1.5rem',marginBottom:'4px'}}>{ic}</div>
                <div style={{fontSize:'12px',fontWeight:600,color:'rgba(255,255,255,.7)',textTransform:'uppercase',letterSpacing:'.06em'}}>{lb}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Panneau droit — formulaire ── */}
      <div style={{
        flex:1, display:'flex', alignItems:'center', justifyContent:'center',
        background:T.bg, padding:'1.5rem',
        minHeight:'min(100vh, 100dvh)',
      }}>
        <div style={{ width:'100%', maxWidth:'380px', animation:'fadeUp .4s ease' }}>

          {/* Logo + titre (mobile) */}
          <div style={{ textAlign:'center', marginBottom:'2rem' }}>
            <div style={{ display:'inline-flex', padding:'14px', background:T.orange, borderRadius:'20px', marginBottom:'14px', boxShadow:`0 6px 20px rgba(240,78,26,.30)` }}>
              <Logo size={40}/>
            </div>
            <div style={{ fontFamily:DISPLAY, fontSize:'2.4rem', letterSpacing:'.06em', color:T.orange, lineHeight:1, marginBottom:'4px' }}>
              Bulléthèque
            </div>
            <div style={{ fontSize:'13px', color:T.muted, fontWeight:500 }}>Ma collection de bandes dessinées</div>
          </div>

          {/* Tabs */}
          {mode !== 'reset' && (
            <div style={{ display:'flex', background:T.g1, borderRadius:'12px', padding:'4px', marginBottom:'1.5rem', border:`1px solid ${T.border}` }}>
              {[['login','Connexion'],['register','Inscription']].map(([v,l])=>(
                <button key={v} onClick={()=>{setMode(v);clear();}} style={{
                  flex:1, padding:'9px', border:'none', borderRadius:'9px',
                  fontFamily:FONT, fontSize:'14px', fontWeight:600, cursor:'pointer',
                  background: mode===v ? T.surface : 'transparent',
                  color: mode===v ? T.orange : T.muted,
                  boxShadow: mode===v ? '0 1px 4px rgba(0,0,0,.08)' : 'none',
                  transition:'all .15s',
                }}>{l}</button>
              ))}
            </div>
          )}

          {/* Erreur */}
          {err && (
            <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'10px', padding:'10px 14px', fontSize:'13px', fontWeight:600, color:T.red, marginBottom:'16px', display:'flex', alignItems:'center', gap:'8px' }}>
              <span>⚠️</span> {err}
            </div>
          )}

          {/* Reset */}
          {mode === 'reset' ? resetOk ? (
            <div style={{textAlign:'center',padding:'1.5rem 0'}}>
              <div style={{fontSize:'2.5rem',marginBottom:'12px'}}>📬</div>
              <div style={{fontSize:'16px',fontWeight:700,color:T.ink,marginBottom:'8px'}}>Email envoyé !</div>
              <div style={{fontSize:'13px',color:T.muted,marginBottom:'1.5rem',lineHeight:1.5}}>Vérifiez votre boîte mail et vos spams.</div>
              <button onClick={()=>{setMode('login');setROk(false);clear();}} style={{padding:'10px 24px',border:'none',borderRadius:'99px',background:T.orange,color:'#fff',fontFamily:FONT,fontSize:'14px',fontWeight:600,cursor:'pointer',boxShadow:`0 4px 12px rgba(240,78,26,.3)`}}>
                ← Retour à la connexion
              </button>
            </div>
          ) : (
            <form onSubmit={reset}>
              <p style={{fontSize:'14px',color:T.muted,marginBottom:'16px',lineHeight:1.5}}>
                Entrez votre email pour recevoir un lien de réinitialisation.
              </p>
              <Field label="Email">
                <input style={inputBase} type="email" value={email} onChange={e=>setEmail(e.target.value)} required placeholder="vous@exemple.fr" onFocus={onFocus} onBlur={onBlur}/>
              </Field>
              <button type="submit" disabled={busy} style={{width:'100%',padding:'12px',border:'none',borderRadius:'10px',background:T.orange,color:'#fff',fontFamily:FONT,fontSize:'15px',fontWeight:600,cursor:'pointer',boxShadow:`0 4px 14px rgba(240,78,26,.3)`,marginBottom:'10px'}}>
                {busy ? '…' : 'Envoyer le lien'}
              </button>
              <button type="button" onClick={()=>{setMode('login');clear();}} style={{width:'100%',padding:'11px',border:`1.5px solid ${T.border}`,borderRadius:'10px',background:'transparent',fontFamily:FONT,fontSize:'14px',fontWeight:600,color:T.muted,cursor:'pointer'}}>
                ← Retour
              </button>
            </form>
          ) : (
            <form onSubmit={submit}>
              {mode==='register' && (
                <Field label="Prénom / Pseudo">
                  <input style={inputBase} type="text" value={name} onChange={e=>setName(e.target.value)} placeholder="Super-Lecteur" onFocus={onFocus} onBlur={onBlur}/>
                </Field>
              )}
              <Field label="Email">
                <input style={inputBase} type="email" value={email} onChange={e=>setEmail(e.target.value)} required placeholder="vous@exemple.fr" onFocus={onFocus} onBlur={onBlur}/>
              </Field>
              <Field label="Mot de passe">
                <input style={inputBase} type="password" value={pw} onChange={e=>setPw(e.target.value)} required placeholder="••••••••" minLength={6} onFocus={onFocus} onBlur={onBlur}/>
              </Field>
              {mode==='login' && (
                <div style={{textAlign:'right',marginTop:'-8px',marginBottom:'16px'}}>
                  <button type="button" onClick={()=>{setMode('reset');clear();}} style={{background:'none',border:'none',fontSize:'13px',fontWeight:600,color:T.orange,cursor:'pointer',fontFamily:FONT}}>
                    Mot de passe oublié ?
                  </button>
                </div>
              )}
              <button type="submit" disabled={busy} style={{
                width:'100%', padding:'13px', border:'none', borderRadius:'10px',
                background:`linear-gradient(135deg,${T.orange},${T.orangeD})`,
                color:'#fff', fontFamily:FONT, fontSize:'15px', fontWeight:700,
                cursor:'pointer', boxShadow:`0 4px 16px rgba(240,78,26,.32)`,
                transition:'transform .15s, box-shadow .15s', marginBottom:'14px',
              }}
                onMouseOver={e=>{e.currentTarget.style.transform='translateY(-1px)';e.currentTarget.style.boxShadow=`0 8px 22px rgba(240,78,26,.42)`;}}
                onMouseOut={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow=`0 4px 16px rgba(240,78,26,.32)`;}}>
                {busy ? '…' : mode==='login' ? 'Se connecter' : 'Créer mon compte'}
              </button>

              <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'14px'}}>
                <div style={{flex:1,height:'1px',background:T.border}}/>
                <span style={{fontSize:'12px',fontWeight:500,color:T.subtle}}>ou</span>
                <div style={{flex:1,height:'1px',background:T.border}}/>
              </div>

              <button type="button" onClick={google} disabled={busy} style={{
                width:'100%', padding:'12px', border:`1.5px solid ${T.border}`, borderRadius:'10px',
                background:T.surface, color:T.ink, fontFamily:FONT, fontSize:'14px', fontWeight:600,
                cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px',
                boxShadow:'0 1px 3px rgba(0,0,0,.06)', transition:'border-color .15s, box-shadow .15s',
              }}
                onMouseOver={e=>{e.currentTarget.style.borderColor=T.muted;e.currentTarget.style.boxShadow='0 2px 8px rgba(0,0,0,.10)';}}
                onMouseOut={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,.06)';}}>
                <svg width="18" height="18" viewBox="0 0 18 18">
                  <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                  <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
                  <path fill="#EA4335" d="M9 3.583c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.583 9 3.583z"/>
                </svg>
                Continuer avec Google
              </button>
            </form>
          )}
        </div>
      </div>

      {/* CSS responsive login panel */}
      <style>{`@media(min-width:768px){.login-panel{display:flex !important}}`}</style>
    </div>
  );
}
